import React, { useEffect, useRef } from 'react';
import { formatTime } from '../utils/gameHelpers';

const GameHeader = ({
  elapsedTime,
  nextExpectedNumber,
  currentSum,
  lastSum,
  showHelpDropdown,
  setShowHelpDropdown,
  onOpenInstructions,
  hardMode,
  setHardMode,
  setSelectedCells,
  onReturnHome,
  puzzleNumber
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowHelpDropdown(false);
      }
    };

    if (showHelpDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelpDropdown, setShowHelpDropdown]);

  return (
    <>
      <img
        src={`${process.env.PUBLIC_URL}/SUMS_logo.png`}
        alt="SUMS"
        style={{ maxWidth: "200px" }}
      />

      <div style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        alignItems: "center",
        columnGap: "6px",
        padding: "2px 0",
        marginBottom: "7px",
        minHeight: "32px"
      }}>
        {/* Clock */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          justifySelf: "start",
          fontSize: "clamp(12px, 3vw, 17px)",
          color: "var(--charcoal)"
        }}>
          <span role="img" aria-label="clock">🕒</span> {formatTime(elapsedTime)}
        </div>

        {/* Centered Info */}
        <div style={{
          fontSize: "clamp(13px, 3.4vw, 21px)",
          display: "flex",
          gap: "clamp(6px, 2vw, 12px)",
          color: "#333",
          justifyContent: "center",
          flexWrap: "wrap",
          minWidth: 0
        }}>
          {nextExpectedNumber != null && (
            <span style={{ color: "var(--charcoal)" }}>
              Next: <strong>{nextExpectedNumber}</strong>
            </span>
          )}
          <span style={{ color: "#666" }}>
            Current: <strong style={{
              color: nextExpectedNumber != null && currentSum > nextExpectedNumber ? "#e53935" : undefined
            }}>{currentSum}</strong>
          </span>
          {lastSum != null && (
            <span style={{ color: "#666" }}>
              Goal: <strong>{lastSum}</strong>
            </span>
          )}
        </div>

        {/* Help icon */}
        <div 
          ref={dropdownRef}
          style={{
            display: "flex",
            alignItems: "center",
            justifySelf: "end",
            color: "var(--charcoal)"
          }}>
          <button
            onClick={() => setShowHelpDropdown(prev => !prev)}
            style={{
              width: "clamp(24px, 6vw, 25px)",
              height: "clamp(24px, 6vw, 25px)",
              fontSize: "clamp(15px, 3.2vw, 19px)",
              borderRadius: "50%",
              border: "2px solid #303036",
              backgroundColor: "#fff",
              fontWeight: "bold",
              cursor: "pointer",
              padding: 0,
              lineHeight: "1",
              textAlign: "center"
            }}
            aria-label="Help"
          >
            <span style={{ color: "#303036" }}><strong>?</strong></span>
          </button>

          {showHelpDropdown && (
            <div
              style={{
                position: "absolute",
                top: "36px",
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "6px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                zIndex: 101,
                minWidth: "160px",
                textAlign: "left"
              }}
            >
              <div
                onClick={() => {
                  setShowHelpDropdown(false);
                  onOpenInstructions();
                }}
                style={{
                  padding: "10px",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                  borderBottom: "1px solid #eee"
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f0f0f0"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "#fff"}
              >
                Instructions
              </div>

              <div style={{
                padding: "10px",
                fontSize: "0.95rem",
                borderBottom: "1px solid #eee"
              }}>
                <div
                  onClick={() => {
                    setHardMode(!hardMode);
                    setSelectedCells([]);
                    setShowHelpDropdown(false);
                  }}
                  style={{
                    display: "flex",
                    width: "100%",
                    height: "32px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "16px",
                    position: "relative",
                    cursor: "pointer",
                    transition: "background-color 0.3s"
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: hardMode ? "calc(50% - 2px)" : "2px",
                      width: "calc(50% - 2px)",
                      height: "28px",
                      backgroundColor: hardMode ? "#e53935" : "#4caf50",
                      borderRadius: "14px",
                      transition: "all 0.3s ease",
                      zIndex: 1
                    }}
                  />

                  <div style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: !hardMode ? "#fff" : "#666",
                    zIndex: 2,
                    transition: "color 0.3s"
                  }}>
                    Normal
                  </div>

                  <div style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: hardMode ? "#fff" : "#666",
                    zIndex: 2,
                    transition: "color 0.3s"
                  }}>
                    <span role="img" aria-label="fire">🔥</span> Hard
                  </div>
                </div>
              </div>

              <div
                onClick={() => {
                  setShowHelpDropdown(false);
                  if (onReturnHome) onReturnHome();
                }}
                style={{
                  padding: "10px",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                  borderBottom: "1px solid #eee"
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f0f0f0"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "#fff"}
              >
                Return to home
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GameHeader;